import React, { useEffect, useMemo, useState } from "react";
import {
  listarPdvs,
  gerarLicenca,
  excluirPdv,
  bloquearLicencaPdv,
  desbloquearLicencaPdv,
} from "../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Key,
  Trash2,
  Edit,
  FileSignature,
  Search,
  ClipboardCopy,
  X,
  ShieldOff,
  Plus,
  Unlock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import BlockModal from "./common/BlockModal";

const DEFAULT_BLOCK_MESSAGE =
  "Entre em contato com o departamento financeiro";

const ListaZenPDV = ({ token }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qp = (k, def = "") => searchParams.get(k) ?? def;

  // filtros (sincronizados com querystring) - SEM revendaId
  const [search, setSearch] = useState(qp("search", ""));
  const [ativo, setAtivo] = useState(qp("ativo", "all")); // all | 1 | 0  (1 = NÃO bloqueado)

  const [pdvs, setPdvs] = useState([]);
  const [selectedPdvs, setSelectedPdvs] = useState([]);
  const [validade, setValidade] = useState("");

  // modais
  const [showModal, setShowModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedPdvToBlock, setSelectedPdvToBlock] = useState(null);
  const [selectedSignature, setSelectedSignature] = useState("");
  const [selectedClientName, setSelectedClientName] = useState("");

  // helper pra status caso backend não tenha mandado ainda
  const computeBloqueadoFallback = (pdv) => {
    const vencida =
      !pdv?.validadeChave || new Date(pdv.validadeChave).getTime() < Date.now();
    return !pdv?.ativo || vencida;
  };

  const fetchPdvs = async () => {
    const resp = await listarPdvs(token, {
      search: search || undefined,
      // revendaId removido -> backend filtra pelo token
      ativo: ativo === "all" ? undefined : ativo,
    });

    setPdvs(Array.isArray(resp.data) ? resp.data : []);
    setSelectedPdvs([]); // limpa seleção quando muda filtro
  };

  useEffect(() => {
    fetchPdvs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, search, ativo]);

  // sync querystring (SEM revendaId)
  useEffect(() => {
    const p = new URLSearchParams(searchParams);

    if (search) p.set("search", search);
    else p.delete("search");

    if (ativo && ativo !== "all") p.set("ativo", ativo);
    else p.delete("ativo");

    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, ativo]);

  // lista final (sem filtro local — backend já filtrou)
  const items = useMemo(() => pdvs, [pdvs]);

  const handleCheckboxChange = (id) => {
    setSelectedPdvs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) setSelectedPdvs(items.map((x) => x.id));
    else setSelectedPdvs([]);
  };

  const handleGerarChave = async () => {
    try {
      for (const pdvId of selectedPdvs) {
        const pdv = items.find((p) => p.id === pdvId);
        if (!pdv) continue;

        const pdvData = {
          Descricao: pdv.descricao,
          CodigoInstalacao: pdv.codigoInstalacao,
          ValidadeChave: validade,
          QtdLicencas: pdv.qtdLicencas,
        };

        await gerarLicenca(pdv.id, pdvData, token);
      }

      alert("Chaves geradas com sucesso!");
      fetchPdvs();
    } catch (error) {
      console.error("Erro ao gerar chaves:", error);
      alert("Erro ao gerar chaves.");
    }
  };

  const handleEdit = (id) => navigate(`/pdvs/editar/${id}`);

  const handleDelete = (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este PDV?")) return;

    excluirPdv(id, token)
      .then(() => {
        alert("PDV excluído com sucesso!");
        fetchPdvs();
      })
      .catch((error) => {
        console.error("Erro ao excluir PDV:", error);
        alert("Erro ao excluir PDV.");
      });
  };

  const handleShowSignature = (signature, clientName) => {
    setSelectedSignature(signature || "");
    setSelectedClientName(clientName || "");
    setShowModal(true);
  };

  const handleBlockLicense = async (message) => {
    if (!selectedPdvToBlock) return;

    try {
      await bloquearLicencaPdv(
        selectedPdvToBlock,
        { MensagemBloqueio: message },
        token
      );
      alert("Licença bloqueada com sucesso!");
      setShowBlockModal(false);
      setSelectedPdvToBlock(null);
      fetchPdvs();
    } catch (error) {
      console.error("Erro ao bloquear licença:", error);
      alert("Erro ao bloquear licença.");
    }
  };

  const handleToggleBlock = async (pdv) => {
    const bloqueado = pdv?.bloqueado ?? computeBloqueadoFallback(pdv);

    if (!bloqueado) {
      setSelectedPdvToBlock(pdv.id);
      setShowBlockModal(true);
      return;
    }

    if (!window.confirm("Tem certeza que deseja desbloquear este PDV?")) return;

    try {
      await desbloquearLicencaPdv(pdv.id, token);
      alert("PDV desbloqueado com sucesso!");
      fetchPdvs();
    } catch (error) {
      console.error("Erro ao desbloquear PDV:", error);
      alert("Erro ao desbloquear PDV.");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(selectedSignature || "");
    alert("Assinatura copiada para a área de transferência!");
  };

  const renderValidade = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("pt-BR");
  };

  return (
    <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Licenças ZenPDV Desktop
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            Filtros: Status (bloqueado = chave vencida ou ativo=false)
          </p>
        </div>

        <button
          className="
            inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm transition
            bg-blue-600 text-white hover:bg-blue-500
          "
          onClick={() => navigate("/pdvs/novo")}
        >
          <Plus size={18} className="mr-2" />
          Novo PDV
        </button>
      </div>

      {/* filtros */}
      <div className="rounded-2xl p-4 mb-6 zen-card border-gray-200 dark:bg-white/5 dark:border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-9">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Cliente / Descrição / Código instalação"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Status
            </label>
            <select
              value={ativo}
              onChange={(e) => setAtivo(e.target.value)}
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                dark:bg-slate-950/35 border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
            >
              <option value="all">Todos</option>
              <option value="1">Ativos (não bloqueados)</option>
              <option value="0">Bloqueados</option>
            </select>
          </div>
        </div>
      </div>

      {/* gerar chave */}
      <div className="rounded-2xl p-4 mb-6 zen-card dark:bg-white/5 dark:border-white/10">
        <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-4">
          Gerar Nova Chave
        </h3>

        <div className="flex flex-col md:flex-row md:items-end md:gap-4 gap-3">
          <div className="flex-grow">
            <label
              htmlFor="validadeChave"
              className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1"
            >
              Nova Data de Validade
            </label>

            <input
              type="date"
              id="validadeChave"
              className="
                w-full px-3 py-2.5 rounded-xl border outline-none transition
                dark:bg-slate-950/35 border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
              value={validade}
              onChange={(e) => setValidade(e.target.value)}
              required
            />
          </div>

          <button
            className="
              inline-flex items-center justify-center px-4 py-2.5 rounded-xl font-semibold text-sm transition
              bg-green-600 text-white hover:bg-green-500
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            onClick={handleGerarChave}
            disabled={selectedPdvs.length === 0 || !validade}
          >
            <Key size={25} className="mr-2" />
            Gerar Chave ({selectedPdvs.length})
          </button>
        </div>
      </div>

      {/* tabela */}
      <div className="overflow-x-auto">
        <div className="min-w-full rounded-2xl zen-card dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="py-3 px-4">
                  <input
                    type="checkbox"
                    className="
                      h-5 w-5 rounded border-gray-300 text-blue-600
                      focus:ring-blue-500/60
                      dark:border-white/20 dark:bg-white/5 dark:focus:ring-blue-400/40
                    "
                    onChange={handleSelectAll}
                    checked={selectedPdvs.length === items.length && items.length > 0}
                  />
                </th>

                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Cliente
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Descrição
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Cód. Instalação
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Licenças
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Vencimento
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Chave Atual
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Status
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Ações
                </th>
              </tr>
            </thead>

            <tbody className="text-gray-700 dark:text-slate-200">
              {items.map((pdv) => {
                const bloqueado = pdv?.bloqueado ?? computeBloqueadoFallback(pdv);

                return (
                  <tr
                    key={pdv.id}
                    className="
                      border-t border-gray-200 hover:bg-gray-50 transition
                      dark:border-white/10 dark:hover:bg-white/5
                    "
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        className="
                          h-5 w-5 rounded border-gray-300 text-blue-600
                          focus:ring-blue-500/60
                          dark:border-white/20 dark:bg-white/5 dark:focus:ring-blue-400/40
                        "
                        checked={selectedPdvs.includes(pdv.id)}
                        onChange={() => handleCheckboxChange(pdv.id)}
                      />
                    </td>

                    <td className="py-3 px-4">{pdv.clienteNome}</td>
                    <td className="py-3 px-4">{pdv.descricao}</td>
                    <td className="py-3 px-4">{pdv.codigoInstalacao}</td>
                    <td className="text-center py-3 px-4">{pdv.qtdLicencas}</td>
                    <td className="py-3 px-4">{renderValidade(pdv.validadeChave)}</td>

                    <td className="py-3 px-4 max-w-xs">
                      <span className="block truncate text-gray-700 dark:text-slate-200">
                        {pdv.chaveAtivacao}
                      </span>
                    </td>

                    <td className="text-center py-3 px-4">
                      {bloqueado ? (
                        <XCircle className="text-red-500 inline" size={20} />
                      ) : (
                        <CheckCircle className="text-green-500 inline" size={20} />
                      )}
                    </td>

                    <td className="text-center py-3 px-4">
                      <div className="inline-flex items-center gap-1">
                        <button
                          className={`
                            p-2 rounded-full transition
                            ${
                              !bloqueado
                                ? "text-red-600 hover:bg-red-100 dark:hover:bg-red-500/10"
                                : "text-green-600 hover:bg-green-100 dark:hover:bg-green-500/10"
                            }
                          `}
                          onClick={() => handleToggleBlock(pdv)}
                          title={!bloqueado ? "Bloquear Licença" : "Desbloquear Licença"}
                        >
                          {!bloqueado ? <ShieldOff size={18} /> : <Unlock size={18} />}
                        </button>

                        <button
                          className="
                            p-2 rounded-full transition
                            text-gray-700 hover:bg-gray-200
                            dark:text-slate-200 dark:hover:bg-white/10
                          "
                          onClick={() => handleShowSignature(pdv.assinaturaSat, pdv.clienteNome)}
                          title="Assinatura SAT"
                        >
                          <FileSignature size={18} />
                        </button>

                        <button
                          className="
                            p-2 rounded-full transition
                            text-blue-600 hover:bg-blue-100
                            dark:text-blue-300 dark:hover:bg-blue-500/10
                          "
                          onClick={() => handleEdit(pdv.id)}
                          title="Alterar Dados"
                        >
                          <Edit size={18} />
                        </button>

                        <button
                          className="
                            p-2 rounded-full transition
                            text-red-600 hover:bg-red-100
                            dark:text-red-300 dark:hover:bg-red-500/10
                          "
                          onClick={() => handleDelete(pdv.id)}
                          title="Excluir PDV"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-gray-500 dark:text-slate-300/80">
                    Nenhum PDV encontrado com os filtros atuais.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* modal assinatura */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg rounded-2xl border shadow-2xl zen-card dark:bg-slate-950/90 dark:border-white/10 dark:backdrop-blur">
            <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-white/10">
              <h5 className="text-xl font-bold text-gray-900 dark:text-white">
                Assinatura SAT
              </h5>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100
                           dark:text-slate-300 dark:hover:text-white dark:hover:bg-white/10"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5">
              <h6 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                Cliente: <span className="font-bold">{selectedClientName}</span>
              </h6>

              <textarea
                readOnly
                value={selectedSignature}
                className="
                  w-full h-40 p-3 rounded-xl border resize-none outline-none
                  bg-white border-gray-300 text-black
                  dark:bg-white/5 dark:border-white/10 dark:text-white
                "
                onFocus={(e) => e.target.select()}
              />

              <div className="flex justify-end mt-4 gap-2">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 rounded-xl font-semibold text-sm transition
                             bg-blue-600 text-white hover:bg-blue-500"
                  onClick={handleCopy}
                >
                  <ClipboardCopy size={18} className="mr-2" />
                  Copiar
                </button>

                <button
                  type="button"
                  className="px-4 py-2 rounded-xl font-semibold text-sm transition border
                             bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
                             dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10"
                  onClick={() => setShowModal(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* modal bloqueio */}
      {showBlockModal && (
        <BlockModal
          show={showBlockModal}
          onClose={() => setShowBlockModal(false)}
          onConfirm={handleBlockLicense}
          defaultMessage={DEFAULT_BLOCK_MESSAGE}
        />
      )}
    </div>
  );
};

export default ListaZenPDV;
